/**
 * App-level effect handlers. Rendered once at the Layout root. Each bridge is
 * a null-rendering component that wires one (or a few) effect types to global
 * concerns: routing, toasts, RTK Query cache invalidation, staged drafts, and
 * scan progress.
 */
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { notification } from 'antd';
import { ReportAPI } from '@/state/slices/reports.ts';
import { StudiesAPI } from '@/state/slices/studies.ts';
import { AssignmentAPI } from '@/state/slices/assignment.ts';
import { stageDraft } from '@/state/slices/copilot/copilotDraft.ts';
import {
  scanStarted,
  scanProgress,
  scanCompleted,
} from '@/state/slices/copilot/copilotScans.ts';
import { useCopilotEffect } from '../useCopilotEffect';

function NavigationBridges() {
  const navigate = useNavigate();
  useCopilotEffect('NAVIGATE_TO_STUDY', (p) =>
    navigate(`/studies/${p.study_uuid}`)
  );
  useCopilotEffect('NAVIGATE_TO_SETTINGS', () => navigate('/settings'));
  return null;
}

function ToastBridge() {
  useCopilotEffect('SHOW_TOAST', (p) => {
    notification[p.level]({ title: p.title, description: p.body });
  });
  return null;
}

function RefreshBridges() {
  const dispatch = useDispatch();
  useCopilotEffect('REFRESH_REPORT', () => {
    dispatch(ReportAPI.util.invalidateTags(['Reports']));
  });
  useCopilotEffect('REFRESH_WORKLIST', () => {
    dispatch(StudiesAPI.util.invalidateTags(['Studies']));
    dispatch(AssignmentAPI.util.invalidateTags(['Assignment']));
  });
  return null;
}

function DraftBridge() {
  const dispatch = useDispatch();
  useCopilotEffect('INSERT_REPORT_DRAFT', (p) => {
    dispatch(
      stageDraft({
        reportUuid: p.report_uuid,
        section: p.section,
        markdown: p.markdown,
      })
    );
  });
  return null;
}

function ScanBridges() {
  const dispatch = useDispatch();
  useCopilotEffect('SCAN_STARTED', (p) =>
    dispatch(
      scanStarted({
        scanId: p.scan_id,
        studyUuid: p.study_uuid,
        product: p.product,
      })
    )
  );
  useCopilotEffect('SCAN_PROGRESS', (p) =>
    dispatch(
      scanProgress({
        scanId: p.scan_id,
        progress: p.progress,
        status: p.status,
      })
    )
  );
  useCopilotEffect('SCAN_COMPLETED', (p) =>
    dispatch(
      scanCompleted({ scanId: p.scan_id, ok: p.ok, reportId: p.report_id })
    )
  );
  return null;
}

// NOTE: SHOW_MEASUREMENT_PLOT / SHOW_COMPARISON_TABLE / REPORT_QUALITY_ISSUES
// are no longer handled here — the provider attaches them to the producing
// assistant message and they render inline via MessageArtifacts.

export function GlobalEffectBridges() {
  return (
    <>
      <NavigationBridges />
      <ToastBridge />
      <RefreshBridges />
      <DraftBridge />
      <ScanBridges />
    </>
  );
}
